// BarcodeCameraScanner — modal that opens the device camera and
// continuously scans for a 1D barcode (EAN-13/UPC-A/etc.) using ZXing.
import React, { useEffect, useRef, useState } from "react";
import { BrowserMultiFormatReader } from "@zxing/browser";
import { BarcodeFormat, DecodeHintType } from "@zxing/library";
import { X, Camera, Loader2, RefreshCw, ScanLine } from "lucide-react";

const FORMATS = [
  BarcodeFormat.EAN_13,
  BarcodeFormat.EAN_8,
  BarcodeFormat.UPC_A,
  BarcodeFormat.UPC_E,
  BarcodeFormat.CODE_128,
  BarcodeFormat.CODE_39,
  BarcodeFormat.ITF,
];

export default function BarcodeCameraScanner({ open, onClose, onDetected }) {
  const videoRef = useRef(null);
  const controlsRef = useRef(null);
  const readerRef = useRef(null);
  const [devices, setDevices] = useState([]);
  const [deviceId, setDeviceId] = useState("");
  const [status, setStatus] = useState("init"); // init | requesting | scanning | error
  const [err, setErr] = useState("");
  const [lastCode, setLastCode] = useState("");

  // List cameras when modal opens
  useEffect(() => {
    if (!open) return;
    setStatus("requesting");
    setErr("");
    (async () => {
      try {
        // Request permission first (Safari needs this before enumerateDevices returns labels)
        const s = await navigator.mediaDevices.getUserMedia({ video: { facingMode: { ideal: "environment" } } });
        s.getTracks().forEach((t) => t.stop());
        const list = await BrowserMultiFormatReader.listVideoInputDevices();
        setDevices(list);
        // Prefer rear camera
        const rear = list.find((d) => /back|rear|environment/i.test(d.label));
        setDeviceId(rear?.deviceId || list[0]?.deviceId || "");
      } catch (e) {
        setStatus("error");
        setErr(e.message || "Kamera icazəsi alınmadı");
      }
    })();
    return () => stopScanner();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Start scanning when device selected
  useEffect(() => {
    if (!open || !deviceId || !videoRef.current) return;
    startScanner(deviceId);
    return () => stopScanner();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, deviceId]);

  const startScanner = async (id) => {
    stopScanner();
    setStatus("scanning");
    setErr("");
    try {
      const hints = new Map();
      hints.set(DecodeHintType.POSSIBLE_FORMATS, FORMATS);
      hints.set(DecodeHintType.TRY_HARDER, true);
      const reader = new BrowserMultiFormatReader(hints, { delayBetweenScanAttempts: 120 });
      readerRef.current = reader;
      const controls = await reader.decodeFromVideoDevice(id, videoRef.current, (result, error) => {
        if (result) {
          const code = result.getText();
          setLastCode(code);
          if (onDetected) onDetected(code);
          stopScanner();
          if (onClose) onClose(code);
        }
      });
      controlsRef.current = controls;
    } catch (e) {
      setStatus("error");
      setErr(e.message || "Kamera başladıla bilmədi");
    }
  };

  const stopScanner = () => {
    try { controlsRef.current?.stop?.(); } catch { /* ignore */ }
    controlsRef.current = null;
    readerRef.current = null;
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[90] flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm" data-testid="barcode-camera-modal">
      <div className="relative w-full max-w-md bg-[var(--surface)] border border-[var(--border)] shadow-2xl">
        <div className="flex items-center justify-between px-4 py-3 border-b border-[var(--border)]">
          <div className="flex items-center gap-2">
            <Camera size={16} className="text-[var(--accent)]" />
            <div className="text-sm font-medium">Barkodu Kameraya Göstərin</div>
          </div>
          <button onClick={() => onClose && onClose(null)} className="text-[var(--text-muted)] hover:text-[var(--text)]" data-testid="scanner-close">
            <X size={18} />
          </button>
        </div>

        <div className="relative bg-black aspect-[4/3] overflow-hidden">
          <video ref={videoRef} className="w-full h-full object-cover" playsInline muted autoPlay />
          {/* Scan line overlay */}
          {status === "scanning" && (
            <>
              <div className="absolute inset-6 border-2 border-white/50 rounded pointer-events-none">
                <div className="absolute left-0 right-0 top-1/2 h-[2px] bg-[var(--accent)] shadow-[0_0_10px_var(--accent)]" style={{ animation: "scanline 2s linear infinite" }} />
              </div>
              <div className="absolute bottom-3 left-0 right-0 text-center text-white text-xs opacity-80 flex items-center justify-center gap-1">
                <ScanLine size={12} /> Barkodu çerçivə daxilində saxlayın
              </div>
            </>
          )}
          {status !== "scanning" && (
            <div className="absolute inset-0 flex items-center justify-center text-white">
              {status === "requesting" ? (
                <div className="text-center">
                  <Loader2 size={22} className="animate-spin mx-auto mb-2" />
                  <div className="text-sm">Kamera icazəsi…</div>
                </div>
              ) : status === "error" ? (
                <div className="text-center px-6">
                  <div className="text-red-400 text-sm mb-2">{err || "Kamera yoxdur"}</div>
                  <div className="text-white/60 text-xs">Brauzerin kameraya icazəsini yoxlayın</div>
                </div>
              ) : null}
            </div>
          )}
        </div>

        {/* Device selector */}
        {devices.length > 1 && (
          <div className="p-3 border-t border-[var(--border)]">
            <label className="text-[10px] tracking-[0.14em] text-[var(--text-muted)] block mb-1">KAMERA</label>
            <div className="flex gap-2">
              <select
                value={deviceId}
                onChange={(e) => setDeviceId(e.target.value)}
                className="input flex-1"
                data-testid="scanner-device"
              >
                {devices.map((d, i) => (
                  <option key={d.deviceId} value={d.deviceId}>
                    {d.label || `Kamera ${i + 1}`}
                  </option>
                ))}
              </select>
              <button onClick={() => startScanner(deviceId)} className="p-2 border border-[var(--border)] hover:border-[var(--accent)]" title="Yenidən başla">
                <RefreshCw size={14} />
              </button>
            </div>
          </div>
        )}

        <div className="p-3 text-[11px] text-[var(--text-muted)] border-t border-[var(--border)]">
          {lastCode ? <>Sonuncu: <span className="font-mono text-[var(--accent)]">{lastCode}</span></> : "Vinyl-in arxasındakı barkodu kameranın qarşısına saxlayın."}
        </div>
      </div>

      <style>{`
        @keyframes scanline {
          0%   { transform: translateY(-80px); opacity: 0.9; }
          50%  { transform: translateY(0);     opacity: 1;   }
          100% { transform: translateY(80px);  opacity: 0.9; }
        }
      `}</style>
    </div>
  );
}
